import React, { useCallback, useEffect, useRef, useState } from 'react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const DRAG_THRESHOLD = 12;
const PAN_SLACK = 0.5;
const CODE_ALIASES = { uk: 'gb' };
const rawPublicUrl = process.env.PUBLIC_URL || '';
const publicUrl = rawPublicUrl === '.' ? '' : rawPublicUrl.replace(/\/$/, '');
const MAP_SRC = `${publicUrl}/world-map.svg`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readCountryCode = (node) => {
  const path = node?.closest?.('path');
  if (!path || path.id === 'world-map') return null;
  const raw = String(path.id || '').toLowerCase();
  if (!raw || raw.startsWith('_')) return null;
  return CODE_ALIASES[raw] || raw;
};

export default function WorldMap({
  disabled = false,
  highlights = [],
  onCountryClick,
}) {
  const viewportRef = useRef(null);
  const svgWrapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [mapReady, setMapReady] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const suppressClickRef = useRef(false);

  zoomRef.current = zoom;
  panRef.current = pan;

  const getPanBounds = useCallback((nextZoom = zoomRef.current) => {
    const viewport = viewportRef.current;
    const canvas = svgWrapRef.current;
    const vw = viewport?.clientWidth || 0;
    const vh = viewport?.clientHeight || 0;
    const cw = canvas?.offsetWidth || vw;
    const ch = canvas?.offsetHeight || vh;
    const overflowX = Math.max(0, (cw * nextZoom - vw) / 2);
    const overflowY = Math.max(0, (ch * nextZoom - vh) / 2);
    const slackX = Math.max(96, vw * PAN_SLACK);
    const slackY = Math.max(96, vh * PAN_SLACK);
    return {
      maxX: overflowX + slackX,
      maxY: overflowY + slackY,
    };
  }, []);

  const clampPan = useCallback((nextPan, nextZoom = zoomRef.current) => {
    const { maxX, maxY } = getPanBounds(nextZoom);
    return {
      x: clamp(nextPan.x, -maxX, maxX),
      y: clamp(nextPan.y, -maxY, maxY),
    };
  }, [getPanBounds]);

  useEffect(() => {
    let cancelled = false;
    fetch(MAP_SRC)
      .then((response) => response.text())
      .then((markup) => {
        if (cancelled || !svgWrapRef.current) return;
        svgWrapRef.current.innerHTML = markup;
        const svg = svgWrapRef.current.querySelector('svg');
        if (svg) {
          svg.classList.add('gtc-world-svg');
          svg.setAttribute('aria-label', 'Clickable world map');
          svg.setAttribute('role', 'img');
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svg.setAttribute('width', '100%');
          svg.setAttribute('height', '100%');
        }
        setMapReady(true);
      })
      .catch(() => {
        if (!cancelled) setMapReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyHighlight = useCallback(() => {
    const root = svgWrapRef.current;
    if (!root) return;
    root.querySelectorAll('path.is-hit-correct, path.is-hit-wrong, path.is-answer').forEach((path) => {
      path.classList.remove('is-hit-correct', 'is-hit-wrong', 'is-answer');
    });
    highlights.forEach((item) => {
      if (!item?.code) return;
      const target = root.querySelector(`path#${CSS.escape(item.code)}`);
      if (!target) return;
      if (item.type === 'correct') target.classList.add('is-hit-correct');
      else if (item.type === 'wrong') target.classList.add('is-hit-wrong');
      else if (item.type === 'answer') target.classList.add('is-answer');
    });
  }, [highlights]);

  useEffect(() => {
    if (mapReady) applyHighlight();
  }, [applyHighlight, mapReady]);

  const setZoomClamped = useCallback((next) => {
    const value = clamp(next, MIN_ZOOM, MAX_ZOOM);
    setZoom(value);
    setPan((current) => clampPan(current, value));
  }, [clampPan]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.12 : 0.9;
      const nextZoom = clamp(zoomRef.current * factor, MIN_ZOOM, MAX_ZOOM);
      setZoom(nextZoom);
      setPan((current) => clampPan(current, nextZoom));
    };

    const onTouchMoveNative = (event) => {
      if (event.touches.length !== 2 || !pinchRef.current) return;
      event.preventDefault();
      const [a, b] = event.touches;
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const ratio = distance / Math.max(1, pinchRef.current.distance);
      const value = clamp(pinchRef.current.zoom * ratio, MIN_ZOOM, MAX_ZOOM);
      setZoom(value);
      setPan((current) => clampPan(current, value));
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('touchmove', onTouchMoveNative, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('touchmove', onTouchMoveNative);
    };
  }, [clampPan]);

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (pinchRef.current) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
      moved: false,
      hitCode: readCountryCode(event.target),
    };
    suppressClickRef.current = false;
  };

  const onPointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      if (!dragRef.current.moved) {
        dragRef.current.moved = true;
        suppressClickRef.current = true;
        setDragging(true);
        try {
          event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch (_error) {
          // jsdom and some browsers throw if pointer capture is unsupported
        }
      }
    }
    if (dragRef.current.moved) {
      setPan(clampPan({
        x: dragRef.current.panX + dx,
        y: dragRef.current.panY + dy,
      }));
    }
  };

  const endDrag = (event, { select }) => {
    const drag = dragRef.current;
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
    if (!select || !drag || drag.moved || drag.pointerId !== event.pointerId) return;
    if (disabled || pinchRef.current || !drag.hitCode) return;
    suppressClickRef.current = true;
    onCountryClick?.(drag.hitCode);
  };

  const onCountrySelect = (event) => {
    if (disabled || pinchRef.current || suppressClickRef.current) return;
    const code = readCountryCode(event.target);
    if (code) onCountryClick?.(code);
  };

  const onTouchStart = (event) => {
    if (event.touches.length === 2) {
      const [a, b] = event.touches;
      pinchRef.current = {
        distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        zoom: zoomRef.current,
      };
      dragRef.current = null;
      setDragging(false);
    }
  };

  const onTouchEnd = (event) => {
    if (event.touches.length < 2) pinchRef.current = null;
  };

  return (
    <div className="gtc-map-shell">
      <div className="gtc-zoom-bar" aria-label="Map zoom">
        <button type="button" className="gtc-zoom-btn" onClick={() => setZoomClamped(zoom - 0.4)} aria-label="Zoom out">
          −
        </button>
        <span className="gtc-zoom-label">{Math.round(zoom * 100)}%</span>
        <button type="button" className="gtc-zoom-btn" onClick={() => setZoomClamped(zoom + 0.4)} aria-label="Zoom in">
          +
        </button>
      </div>

      <div
        ref={viewportRef}
        data-testid="gtc-map-viewport"
        className={`gtc-map-viewport${disabled ? ' is-disabled' : ''}${dragging ? ' is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(event) => endDrag(event, { select: true })}
        onPointerCancel={(event) => endDrag(event, { select: false })}
        onClick={onCountrySelect}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          ref={svgWrapRef}
          className="gtc-map-canvas"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        />
      </div>
    </div>
  );
}
